<script setup lang="ts">
// Page V2 « Contacts » — liste paginée des contacts (non archivés) côté serveur,
// avec recherche CONTACT-ONLY (pas l'omnisearch global), tri par colonne et pagination.
// Sert aussi de cible de drill-down depuis la recherche globale : /contacts?q=…
import { onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UsersIcon,
} from '@heroicons/vue/24/outline'
import { useContactsListStore } from '@/v2/stores/useContactsListStore'
import type { ContactListItem, ContactSortField } from '@/api/contactsApi'

const route = useRoute()
const router = useRouter()
const store = useContactsListStore()
const { q, page, pageCount, total, from, to, rows, loading, error, sort, order } = storeToRefs(store)

const COLUMNS: { field: ContactSortField; label: string; sortable: boolean }[] = [
  { field: 'lastname', label: 'Nom', sortable: true },
  { field: 'firstname', label: 'Prénom', sortable: true },
  { field: 'email', label: 'Email', sortable: true },
]

// Étiquette statut métier de la SOCIÉTÉ du contact (currentStatus, minuscule Odoo).
const STATUS_LABEL: Record<string, string> = {
  client: 'Client',
  prospect: 'Prospect',
  suspect: 'Suspect',
}
const STATUS_BADGE: Record<string, string> = {
  client: 'badge-success',
  prospect: 'badge-info',
  suspect: 'badge-ghost',
}

function onInput(e: Event) {
  store.setQuery((e.target as HTMLInputElement).value)
}

function goTo(item: ContactListItem) {
  if (item.uuid) router.push(`/dashboard/contacts/${item.uuid}`)
}

// Sync q ↔ URL (liens partageables + drill-down omnisearch)
watch(q, (value) => {
  router.replace({ query: { ...route.query, q: value || undefined } })
})

onMounted(() => {
  store.init((route.query.q as string | undefined) ?? '')
})
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header class="mb-6">
      <h1 class="flex items-center gap-2 text-2xl font-bold">
        <UsersIcon class="h-6 w-6 text-primary" aria-hidden="true" />
        Contacts
      </h1>
      <p class="mt-1 text-base-content/70">Liste de vos contacts — recherche par nom, prénom, email ou téléphone.</p>
    </header>

    <!-- Barre de recherche contact-only -->
    <div
      class="mb-4 flex items-center gap-3 rounded-xl border border-base-300 bg-base-100 px-4 py-2.5 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
    >
      <MagnifyingGlassIcon
        class="h-5 w-5 shrink-0"
        :class="loading ? 'text-primary' : 'text-base-content/40'"
        aria-hidden="true"
      />
      <input
        type="text"
        class="flex-1 bg-transparent text-base text-base-content outline-none placeholder:text-base-content/30"
        placeholder="Rechercher un contact (nom, prénom, email, téléphone)…"
        :value="q"
        aria-label="Rechercher un contact"
        @input="onInput"
      />
      <ArrowPathIcon v-if="loading" class="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
      <button
        v-if="q"
        type="button"
        class="flex h-6 w-6 items-center justify-center rounded-full text-base-content/40 transition-colors hover:bg-base-200 hover:text-base-content"
        aria-label="Effacer la recherche"
        @click="store.setQuery('')"
      >
        <XMarkIcon class="h-4 w-4" />
      </button>
    </div>

    <!-- Erreur -->
    <div v-if="error" role="alert" class="alert alert-error">
      <span>Une erreur est survenue lors du chargement des contacts.</span>
      <button type="button" class="btn btn-sm" @click="store.reload()">Réessayer</button>
    </div>

    <template v-else>
      <!-- Chargement initial -->
      <div
        v-if="loading && !rows.length"
        class="rounded-xl border border-base-200 bg-base-100 py-10 text-center shadow-sm"
      >
        <span class="loading loading-spinner" aria-hidden="true"></span>
        <p class="mt-2 text-base-content/70">Chargement des contacts…</p>
      </div>

      <!-- Aucun résultat -->
      <div
        v-else-if="!rows.length"
        class="rounded-xl border border-base-200 bg-base-100 py-10 text-center text-base-content/70 shadow-sm"
      >
        <template v-if="q">Aucun contact pour « {{ q }} ».</template>
        <template v-else>Aucun contact.</template>
      </div>

      <template v-else>
        <!-- Tri (mobile uniquement — sur ≥ md le tri se fait via les en-têtes de colonne) -->
        <div class="mb-3 flex items-center gap-2 md:hidden">
          <label for="contacts-mobile-sort" class="shrink-0 text-sm text-base-content/70">Trier :</label>
          <select
            id="contacts-mobile-sort"
            class="select select-bordered flex-1 text-base"
            :value="sort"
            @change="store.setSort(($event.target as HTMLSelectElement).value as ContactSortField)"
          >
            <option v-for="col in COLUMNS" :key="col.field" :value="col.field">{{ col.label }}</option>
          </select>
          <button
            type="button"
            class="btn btn-square"
            :aria-label="
              order === 'asc'
                ? 'Ordre croissant — changer en décroissant'
                : 'Ordre décroissant — changer en croissant'
            "
            @click="store.setSort(sort)"
          >
            <ChevronUpIcon v-if="order === 'asc'" class="h-5 w-5" aria-hidden="true" />
            <ChevronDownIcon v-else class="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <!-- Tableau (tablette / desktop ≥ md) -->
        <div class="hidden overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm md:block">
          <table class="table">
            <thead>
              <tr>
                <th v-for="col in COLUMNS" :key="col.field" scope="col">
                  <button
                    v-if="col.sortable"
                    type="button"
                    class="flex items-center gap-1 font-semibold hover:text-primary"
                    @click="store.setSort(col.field)"
                  >
                    {{ col.label }}
                    <ChevronUpIcon v-if="sort === col.field && order === 'asc'" class="h-3.5 w-3.5" aria-hidden="true" />
                    <ChevronDownIcon v-else-if="sort === col.field && order === 'desc'" class="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  <span v-else>{{ col.label }}</span>
                </th>
                <th scope="col">Téléphone</th>
                <th scope="col">Mobile</th>
                <th scope="col">Société</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in rows"
                :key="item.uuid"
                class="hover cursor-pointer"
                @click="goTo(item)"
              >
                <td class="font-medium">{{ item.lastname ?? '—' }}</td>
                <td>{{ item.firstname ?? '—' }}</td>
                <td>{{ item.email ?? '—' }}</td>
                <td class="tabular-nums">{{ item.phone ?? '—' }}</td>
                <td class="tabular-nums">{{ item.mobile ?? '—' }}</td>
                <td>
                  <template v-if="item.company">
                    <span>{{ item.company.name ?? '—' }}</span>
                    <span
                      v-if="item.company.currentStatus"
                      class="badge badge-sm ml-2"
                      :class="STATUS_BADGE[item.company.currentStatus.toLowerCase()] ?? 'badge-ghost'"
                    >
                      {{ STATUS_LABEL[item.company.currentStatus.toLowerCase()] ?? item.company.currentStatus }}
                    </span>
                  </template>
                  <template v-else>—</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cartes (mobile < md) — le tableau dense est illisible sur petit écran -->
        <ul role="list" class="flex flex-col gap-3 md:hidden">
          <li v-for="item in rows" :key="item.uuid">
            <button
              type="button"
              class="flex w-full flex-col gap-2 rounded-xl border border-base-200 bg-base-100 p-4 text-left shadow-sm transition-colors hover:border-primary/40 focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transition-none"
              @click="goTo(item)"
            >
              <div class="flex items-start justify-between gap-2">
                <span class="font-semibold text-base-content">
                  {{ [item.firstname, item.lastname].filter(Boolean).join(' ') || '—' }}
                </span>
                <span
                  v-if="item.company?.currentStatus"
                  class="badge badge-sm shrink-0"
                  :class="STATUS_BADGE[item.company.currentStatus.toLowerCase()] ?? 'badge-ghost'"
                >
                  {{ STATUS_LABEL[item.company.currentStatus.toLowerCase()] ?? item.company.currentStatus }}
                </span>
              </div>
              <p v-if="item.company?.name" class="text-sm text-base-content/70">{{ item.company.name }}</p>
              <dl class="flex flex-col gap-1 text-sm">
                <div class="flex gap-1">
                  <dt class="shrink-0 text-base-content/70">Email :</dt>
                  <dd class="break-all text-base-content">{{ item.email ?? '—' }}</dd>
                </div>
                <div class="flex gap-1">
                  <dt class="shrink-0 text-base-content/70">Tél :</dt>
                  <dd class="tabular-nums text-base-content">{{ item.phone ?? '—' }}</dd>
                </div>
                <div class="flex gap-1">
                  <dt class="shrink-0 text-base-content/70">Mobile :</dt>
                  <dd class="tabular-nums text-base-content">{{ item.mobile ?? '—' }}</dd>
                </div>
              </dl>
            </button>
          </li>
        </ul>
      </template>
    </template>

    <!-- Pagination -->
    <div v-if="!error" class="mt-4 flex flex-wrap items-center justify-between gap-3">
      <span class="text-sm text-base-content/70">
        <template v-if="total > 0">{{ from }}–{{ to }} sur {{ total }} contact{{ total > 1 ? 's' : '' }}</template>
        <template v-else>0 contact</template>
      </span>
      <div class="join">
        <button
          type="button"
          class="btn btn-sm join-item"
          :disabled="page <= 1 || loading"
          aria-label="Page précédente"
          @click="store.setPage(page - 1)"
        >
          <ChevronLeftIcon class="h-4 w-4" aria-hidden="true" />
        </button>
        <span class="btn btn-sm join-item pointer-events-none">Page {{ page }} / {{ pageCount }}</span>
        <button
          type="button"
          class="btn btn-sm join-item"
          :disabled="page >= pageCount || loading"
          aria-label="Page suivante"
          @click="store.setPage(page + 1)"
        >
          <ChevronRightIcon class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>
